import { debounceTime, Subject } from "rxjs";
import { inject, injectable } from "tsyringe";
import { ExtensionContext } from "../../common/models/extension-config";
import { Module } from "../../common/module";
import { CommonModule } from "../common.module";
import { ToolbarVersion } from "./api/autogenerated";
import { UrlChangeListenerModule } from "./url-change-listener.module";

@injectable()
export class DashboardTooltipButtonManagerModule extends Module {
    public name: string = 'DashboardTooltipButtonManagerModule';
    public acceptedLicenseFlags: string[] = [];

    constructor(@inject(UrlChangeListenerModule) private urlListenerModule: UrlChangeListenerModule,
        @inject(CommonModule) private common: CommonModule) {
        super([urlListenerModule, common]);
    }
    // this only runs if it's a dependency. does not run on its own
    public shouldRun(context: ExtensionContext): boolean { return false; }

    // All functions will next() this to request drawButtons. The actual work will subscribe to this. This is used to filter flooded requests later
    private readonly drawButtonsRequest = new Subject<void>();
    public async run(): Promise<void> {
        // draw buttons when no new requests arrived in 300ms
        this.drawButtonsRequest.pipe(debounceTime(300)).subscribe(() => this.drawButtons());
        // request re-draw buttons when url changes. This will also fire instantly
        this.urlListenerModule.event.subscribe(() => this.drawButtonsRequest.next());
        window.addEventListener('resize', () => this.resizeHandler());
    }

    // ### HTML SELECTORS

    private readonly toolbarRightSelector = '.tab-nonVizItems,#viz-viewer-toolbar > div:nth-child(2)';
    private readonly toolbarLeftSelector = '.tab-vizItems,#viz-viewer-toolbar > div:nth-child(1)';

    private readonly buttonIdAttribute = 'elements-bmgr-id';
    private readonly buttonIdAttributeSelector = '[elements-bmgr-id]';
    private buttonSelector(id: string) { return '[elements-bmgr-id="' + id + '"]'; }

    // ### REGISTRATIONS
    // contains a list of functions, that return a Promise that resolve to a HTML button (or undefined) that can be added to the tooltip
    private readonly registeredButtonFns: { [key: string]: (urlData: UrlData, version: ToolbarVersion) => Promise<Element | undefined> } = {};
    private registeredButtonFnsIdCounter = 0;

    // ### Registrations (public methods). These are called from external JS code to register a button to be added to the toolbar

    /**
     * Registers a button to be added to the toolbar. 
     * @param promiseToGetButtonHtml - must be a function that returns a Promise that resolves to the button DOM element
     * @returns the registration ID
     */
    public registerButtonFn(promiseToGetButtonHtml: (urlData: UrlData, version: ToolbarVersion) => Promise<Element | undefined>): number {
        this.registeredButtonFns[++this.registeredButtonFnsIdCounter] = promiseToGetButtonHtml;
        this.drawButtonsRequest.next();
        return this.registeredButtonFnsIdCounter;
    }

    /**
     * Unregisters a button on the toolbar. The button will be removed if visible and will not be drawn again
     * @param registrationId The registration ID that was returned with the registerButtonFn
     */
    public unRegisterButtonFn(registrationId: number) {
        this.registeredButtonFnsIdCounter++;
        delete this.registeredButtonFns[registrationId];
        this.drawButtonsRequest.next();
    }

    private lastDrawButtonsData: { lastFnId: number, url: UrlData } | null = null;
    // ### Draw buttons. This will add all the missing buttons to the toolbar, if we are on a dashboard page. It also removes unregistered buttons
    private async drawButtons() {
        // get URL information. This returns the site, workbook and view names
        let urlData = this.getSiteAndWorkbookNameFromUrlHash(location.hash);
        if (!urlData) {
            // we were unsuccessful with hash, try path name
            urlData = this.getSiteAndWorkbookNameFromUrlHash('#' + location.pathname);
            if (!urlData) {
                // we were unsuccessful. clear the last draw buttons data and return
                this.lastDrawButtonsData = null;
                return;
            }
        }

        try {
            const domInfo = await this.getTableauDocAndToolbar();
            const l = this.lastDrawButtonsData;
            // if we are on the same dashboard as before AND the toolbar has been touched (our buttons still exist)
            if (domInfo.toolbarTouched && l?.url.site === urlData.site && l?.url.workbook === urlData.workbook && l?.url.view === urlData.view && l.lastFnId === this.registeredButtonFnsIdCounter) {
                return;
            }
            // ### We are on a dashoard and we need to re-draw. 

            // we apply a MutationObserver first to the toolbar to ensure that our buttons do not get deleted by Tableau Angular
            this.applyMutationObserver(domInfo.toolbar);

            // we will not redraw all buttons (causes flicker), but we will add/remove only the ones that are needed
            var registeredButtonIds = Object.keys(this.registeredButtonFns);
            // Remove all buttons that are deleted from registeredButtonFns
            const existingButtons = domInfo.doc.querySelectorAll(this.buttonIdAttributeSelector);
            for (let i = 0; i < existingButtons.length; i++) {
                const buttonId = existingButtons[i].getAttribute(this.buttonIdAttribute)!;
                if (registeredButtonIds.indexOf(buttonId) === -1) {
                    existingButtons[i].removeAttribute(this.buttonIdAttribute);
                    // console.log('remove button', buttonId, existingButtons[i]);
                    existingButtons[i].remove();
                }
            }

            // get missing buttons
            const missingButtonFns = this._getMissingButtonFns(registeredButtonIds, domInfo.doc);
            const missingButtonKeys = Object.keys(missingButtonFns);

            // get tooltip version
            const tableauVersion = this.common.getTableauApiVersion();
            let toolbarVersion: ToolbarVersion;
            if (tableauVersion.major < 3) {
                toolbarVersion = ToolbarVersion.Pre2022;
            } else if (tableauVersion.major > 3) {
                toolbarVersion = ToolbarVersion.Post2022;
            } else {
                toolbarVersion = tableauVersion.minor >= 15 ? ToolbarVersion.Post2022: ToolbarVersion.Pre2022;
            }

            // we got the buttons we need to add. Request their HTML in parallel
            const results = await Promise.all(missingButtonKeys.map(key => missingButtonFns[key](urlData!, toolbarVersion).catch(e => {
                console.log(`Failed to resolve button HTML for id: ${key}`, e);
                return undefined;
            })));

            // get the missing button fns again. It may be that some buttons were drawn as we were resolving the promises
            const newMissingButtonFns = this._getMissingButtonFns(registeredButtonIds, domInfo.doc);

            for (let i = 0; i < results.length; i++) {
                const buttonElement = results[i];
                if (!buttonElement) {
                    continue;
                }
                const buttonKey = missingButtonKeys[i];
                if (!newMissingButtonFns[buttonKey]) {
                    // if this button already exists, skip (it might have been created while promises were resolving)
                    continue;
                }
                buttonElement.setAttribute(this.buttonIdAttribute, buttonKey);
                this._insertButton(domInfo.toolbar, buttonElement);
            }
            this.resizeHandler();
            this.lastDrawButtonsData = {
                lastFnId: this.registeredButtonFnsIdCounter,
                url: urlData
            };
        } catch (e: any) {
            console.error(`Failed to draw buttons: ${e.message}`, e.stack);
        }
    }
    private _getMissingButtonFns(registeredButtonIds: string[], doc: Document) {
        const missingButtonFns: { [key: string]: (urlData: UrlData, version: ToolbarVersion) => Promise<Element | undefined> } = {};
        for (let i = 0; i < registeredButtonIds.length; i++) {
            const id = registeredButtonIds[i];
            var existingButton = doc.querySelector(this.buttonSelector(id));
            if (!existingButton) {
                missingButtonFns[id] = this.registeredButtonFns[id];
            }
        }
        return missingButtonFns;
    }
    private _insertButton(toolbar: Element, button: Element) {
        var buttonId = +button.getAttribute(this.buttonIdAttribute)!;
        var targetBeforeIndex = 0;
        for (let toolbarChild of toolbar.children) {
            const toolbarChildId = toolbarChild.getAttribute(this.buttonIdAttribute);
            if (toolbarChildId) {
                const i = +toolbarChildId;
                if (i <= buttonId) {
                    targetBeforeIndex++;
                }
            }
        }
        toolbar.insertBefore(button, toolbar.children[targetBeforeIndex]);
    }
    /**
     * Gets the site name and workbook name from the current URL hash
     * @param urlHash - The hash to extract from. Must begin with '#'
     */
    private getSiteAndWorkbookNameFromUrlHash(urlHash: string): UrlData | null {
        if (!urlHash) {
            return null;
        }
        if (urlHash.startsWith("#/site/")) {
            const urlHashParts = urlHash.split("/");
            if (urlHashParts.length > 4 && urlHashParts[3] === "views") {
                return {
                    site: urlHashParts[2],
                    workbook: urlHashParts[4],
                    view: /^([^\?]*)/.exec(urlHashParts[5] || '')![1]
                };
            }
        } else if (urlHash.startsWith("#/views/")) {
            const urlHashParts = urlHash.split("/")
            if (urlHashParts.length > 2) {
                return {
                    site: "", // Note: empty string is needed for Default Site
                    workbook: urlHashParts[2],
                    view: /^([^\?]*)/.exec(urlHashParts[3] || '')![1]
                }
            }
        }
        return null;
    }

    // ### Tableau UI Components specific code
    private async getTableauDocAndToolbar(waitSeconds: number = 60): Promise<{ doc: Document, toolbar: Element, toolbarTouched: boolean }> {
        const doc = await this.common.getDashboardDocument();
        if (doc === null) {
            throw new Error("Dashboard document was not found");
        }
        const toolbar = await this.common.waitForElement(this.toolbarRightSelector, waitSeconds, doc.body);
        if (toolbar === null) {
            throw new Error("Dashboard toolbar was not found");
        }
        return {
            toolbar, doc, toolbarTouched: this.getAndSetToolbarTouched(toolbar)
        };
    }
    private getAndSetToolbarTouched(toolbar: Element) {
        var touched = !!toolbar.getAttribute('button-manager-touched');
        toolbar.setAttribute('button-manager-touched', 'true');
        return touched;
    }


    // ### MutationObserver. Ensures that if Tableau Angular removes our buttons, they will be re-added

    private _currentlyObserving: Element | null = null;
    private _observer: MutationObserver | undefined = undefined;
    /**
     * Applies MutationObserver to the current toolbar if not already applied
     * @param toolbar 
     */
    private applyMutationObserver(toolbar: Element) {
        if (this._currentlyObserving === toolbar) {
            return;
        }
        if (this._observer) {
            this._observer.disconnect();
        }
        this._observer = new MutationObserver(records => {
            // go through all records
            for (let record of records) {
                // go through all removed nodes
                let hasReInserted = false;
                for (let removedNode of record.removedNodes) {
                    // if removed node is a button manager button, re-draw (unless it has been unregistered)
                    const buttonId = (removedNode as Element)?.getAttribute(this.buttonIdAttribute)
                    if (buttonId && this.registeredButtonFns[buttonId]) {
                        hasReInserted = true;
                        this._insertButton(record.target as Element, removedNode as Element);
                    }
                }
                if (hasReInserted) {
                    this.resizeHandler();
                }
            }
        });
        this._observer.observe(toolbar, {
            subtree: false,
            childList: true,
            attributes: false
        });
    }

    private async resizeHandler() {
        // This will get the doc and toolbar separately and return if they do not exist (it does not wait)

        const doc = await this.common.getDashboardDocument(0);
        if (!doc) {
            return;
        }
        const toolbar = doc.querySelector(this.toolbarRightSelector);
        const leftToolbar = doc.querySelector(this.toolbarLeftSelector);
        if (!toolbar || !leftToolbar) {
            return;
        }

        // 1. First, get all custom buttons
        const customButtons = toolbar.querySelectorAll(this.buttonIdAttributeSelector);
        // if there are no custom buttons, return
        if (customButtons.length === 0) {
            return;
        }
        // 2. Show all custom buttons
        customButtons.forEach(b => this._showButton(b));
        // 3. Check if Tableau buttons are collapsed.
        if (toolbar.classList.contains('hideLabels') || toolbar.parentElement!.classList.contains('hideLabels')) {
            // if Tableau buttons are collapsed, collapse our buttons too
            customButtons.forEach(b => this._lockButtonInIconMode(b));
        } else {
            // 3.1 Tableau buttons are in text mode. Make our buttons text mode
            customButtons.forEach(b => this._lockButtonInTextMode(b));
            // 3.2 start a loop to check if we are colliding on the toolbar. If so, lock individual buttons into icon mode
            let lastLockedIndex = -1;
            while (this._isCollideWithCorrection(leftToolbar, toolbar, toolbar.parentElement!.offsetWidth, 5)) {
                let toLockIndex = lastLockedIndex + 1;
                if (customButtons.length <= toLockIndex) {
                    break;
                }
                this._lockButtonInIconMode(customButtons[toLockIndex]);
                lastLockedIndex = toLockIndex;
            }
        }
        // at this point, we have collapsed as many buttons as needed. If there is still collision of the toolbars, we need to start removing buttons
        // 4. Start a loop to check when the buttons fit
        let lastDeletedIndex = -1;
        while (this._isCollideWithCorrection(leftToolbar, toolbar, toolbar.parentElement!.offsetWidth, 5)) {
            let toDeleteIndex = lastDeletedIndex + 1;
            if (customButtons.length <= toDeleteIndex) {
                // we have no more buttons to delete
                break;
            }
            // delete custom button 
            this._hideButton(customButtons[toDeleteIndex]);
            lastDeletedIndex = toDeleteIndex;
        }
    }

    private _setStyle(element: Element | null, a: (e: CSSStyleDeclaration) => void) {
        if (element && <unknown>element as ElementCSSInlineStyle) {
            a((<ElementCSSInlineStyle><unknown>element).style);
        }
    }
    private _lockButtonInIconMode(button: Element) {
        this._setStyle(button.querySelector('.tabToolbarButtonText'), e => e.display = 'none');
        this._setStyle(button.querySelector('.tabToolbarButtonImg'), e => e.width = '25px');
        this._setStyle(button, e => e.justifyContent = 'center');

    }
    private _lockButtonInTextMode(button: Element) {
        this._setStyle(button.querySelector('.tabToolbarButtonText'), e => e.display = 'inline-block');
        this._setStyle(button.querySelector('.tabToolbarButtonImg'), e => e.width = '');
        this._setStyle(button, e => e.justifyContent = '');
    }
    private _hideButton(button: Element) {
        this._setStyle(button, a => {
            a.minWidth = '0px';
            a.maxWidth = '0px';
            a.padding = '0px';
            a.border = 'none';
        });

    }
    private _showButton(button: Element) {
        this._setStyle(button, a => {
            a.width = '';
            a.maxWidth = '';
            a.minWidth = '';
            a.padding = '';
            a.border = '';
        });

    }
    private _isCollideWithCorrection(a: Element, b: Element, parentWidth: number, bHorizontalLeftCorrection: number) {
        var aRect = a.getBoundingClientRect();
        var bRect = b.getBoundingClientRect();
        return (aRect.width + bRect.width + bHorizontalLeftCorrection) > parentWidth;
    }
    public loadingSnippet = '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" style="margin: auto; display: block; shape-rendering: auto;" width="24px" height="24px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid">' +
    '<g transform="rotate(0 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.9166666666666666s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(30 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.8333333333333334s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(60 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.75s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(90 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.6666666666666666s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(120 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.5833333333333334s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(150 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.5s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(180 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.4166666666666667s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(210 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.3333333333333333s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(240 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.25s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(270 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.16666666666666666s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(300 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="-0.08333333333333333s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<g transform="rotate(330 50 50)">' +
    '<rect x="47" y="10.5" rx="3" ry="9.5" width="6" height="19" fill="#355c80">' +
    '<animate attributeName="opacity" values="1;0" keyTimes="0;1" dur="1s" begin="0s" repeatCount="indefinite"></animate>' +
    '</rect>' +
    '</g>' +
    '<!-- [ldio] generated by https://loading.io/ -->' +
    '</svg>';
}

export interface UrlData {
    site: string;
    workbook: string;
    view: string;
}
